import { useState, useEffect, useCallback, useMemo } from "react";
import firebase from "firebase/compat/app";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import {
  USER_NOTES_COLLECTION_NAME,
  UserNote,
  UserNotesDocument,
  userNotesDocId
} from "./../@types/sharedSchema";
import { applyNoteShare, withDefaultNoteShare } from "utils/noteSharing";

/**
 * A `UserNote` enriched with authorship metadata so the popover can render
 * teammates' org-shared notes alongside the current user's own, with the
 * right UI affordances (no edit/delete on others', author label visible).
 */
export type DisplayUserNote = UserNote & {
  isMine: boolean;
  /** Email of the note's author — `userEmail` for own notes. */
  authorEmail: string;
  /** Resolved from `organizationUsers` when available; falls back to email. */
  authorName?: string;
  /**
   * Set on a note written on a PROPERTY and shown on its owner's contact card (the contact's
   * `sellingListingIds` / `rentingOutListingIds` — see `usePropertyNotes`). Read-only there: it
   * lives on the property, and is edited where it was written. `label` is the property's address
   * when it is still known (an ad taken down has none).
   */
  listing?: { listingId: string; label?: string };
};

type UseListingNotesReturn = {
  notes: DisplayUserNote[];
  hasNotes: boolean | undefined;
  isLoading: boolean;
  addNote: (text: string) => void;
  updateNote: (noteId: string, text: string) => void;
  deleteNote: (noteId: string) => void;
  setNoteOrgShare: (noteId: string, share: boolean) => void;
};

/**
 * Recompute the doc-level org union from the notes array. Returns `undefined`
 * (NOT `[]`) when no note is org-shared, so callers can omit the field on
 * write and Firestore's `set()` (no merge) removes it entirely. This matters
 * for the read path: the `array-contains` query and the firestore.rules
 * check both rely on the field being absent rather than empty.
 */
function computeDocOrgIds(notes: UserNote[]): string[] | undefined {
  const set = new Set<string>();
  for (const n of notes) {
    for (const orgId of n.organizationIds ?? []) set.add(orgId);
  }
  return set.size > 0 ? Array.from(set) : undefined;
}

/**
 * Real-time per-user notes for a single listing.
 * Stored in the shared `user-notes` collection under a listing-scoped doc id.
 */
export function useListingNotes(
  listingId: string | undefined,
  enabled: boolean
): UseListingNotesReturn {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, organizations, organizationUsers } = useFirestore();

  // Mine: my single doc (read by docId for cheapness).
  const [myNotes, setMyNotes] = useState<UserNote[]>([]);
  // Teammates': any other user's doc where a note was shared with my org
  // and tagged with THIS listingId. Queried separately because Firestore
  // can't `OR` across the two predicates (`userId == me` vs
  // `organizationIds array-contains myOrg`).
  const [orgScopedNotes, setOrgScopedNotes] = useState<DisplayUserNote[]>([]);
  const [hasNotes, setHasNotes] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Listener 1 — the current user's own notes doc for this listing.
  useEffect(() => {
    if (!listingId || !enabled || !userEmail) return;

    setIsLoading(true);

    const docId = userNotesDocId({ userId: userEmail, listingId });
    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .doc(docId)
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.exists ? (snapshot.data() as UserNotesDocument) : undefined;
          const parsed = (data?.notes ?? []).map((n) => ({
            ...n,
            noteTime: (n.noteTime as any)?.toDate?.() ?? new Date(n.noteTime)
          }));
          setMyNotes(parsed);
          setIsLoading(false);
        },
        (error) => {
          console.error("useListingNotes snapshot error", error);
          setIsLoading(false);
        }
      );

    return () => unsubscribe();
  }, [enabled, listingId, userEmail]);

  // Listener 2 — other users' docs that have at least one note shared with
  // the current user's org for this listing. We further filter the notes
  // array per-doc to keep only those individual notes the author opted to
  // share (since the doc-level `organizationIds` is the union — a doc may
  // have private notes mixed in with shared ones).
  useEffect(() => {
    if (!listingId || !enabled || !organizationId || !userEmail) return;

    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("listingId", "==", listingId)
      .where("organizationIds", "array-contains", organizationId)
      .onSnapshot(
        (snapshot) => {
          const collected: DisplayUserNote[] = [];
          snapshot.docs.forEach((d) => {
            const data = d.data() as UserNotesDocument;
            // Skip own doc — listener 1 already handles it.
            if (data.userId === userEmail) return;
            for (const n of data.notes ?? []) {
              if (!(n.organizationIds ?? []).includes(organizationId)) continue;
              const noteTime = (n.noteTime as any)?.toDate?.() ?? new Date(n.noteTime);
              collected.push({
                ...n,
                noteTime,
                isMine: false,
                authorEmail: data.userId
              });
            }
          });
          setOrgScopedNotes(collected);
        },
        (error) => {
          console.error("useListingNotes org-notes error", error);
        }
      );

    return () => unsubscribe();
  }, [enabled, listingId, organizationId, userEmail]);

  // Merge mine + teammates' shared, sort by recency, resolve display names.
  const notes = useMemo<DisplayUserNote[]>(() => {
    const nameByEmail = new Map<string, string>();
    for (const u of organizationUsers ?? []) {
      if (u.email) nameByEmail.set(u.email, u.name || u.email);
    }
    const mineWithMeta: DisplayUserNote[] = myNotes.map((n) => ({
      ...n,
      isMine: true,
      authorEmail: userEmail ?? ""
    }));
    const merged = [...mineWithMeta, ...orgScopedNotes].sort(
      (a, b) => (b.noteTime?.getTime() ?? 0) - (a.noteTime?.getTime() ?? 0)
    );
    return merged.map((n) =>
      n.isMine
        ? n
        : { ...n, authorName: nameByEmail.get(n.authorEmail) ?? n.authorEmail }
    );
  }, [myNotes, orgScopedNotes, organizationUsers, userEmail]);

  useEffect(() => {
    setHasNotes(notes.length > 0);
  }, [notes.length]);

  /**
   * Whole-document rewrite. Only safe for edit/delete/share, which act on a
   * note the user is looking at — so listener 1 has necessarily delivered and
   * `myNotes` is current. ADDING goes through `addNote`'s `arrayUnion` instead,
   * because that is the one mutation reachable before the first snapshot.
   */
  const saveNotes = useCallback(
    (updatedNotes: UserNote[]) => {
      if (!listingId || !userEmail) return;
      const docId = userNotesDocId({ userId: userEmail, listingId });
      const docOrgIds = computeDocOrgIds(updatedNotes);
      const docData: UserNotesDocument = {
        userId: userEmail,
        listingId,
        notes: updatedNotes,
        // Conditionally include organizationIds so toggling the last share
        // OFF actually removes the field from the doc (set() without merge
        // replaces the doc, dropping omitted fields).
        ...(docOrgIds ? { organizationIds: docOrgIds } : {})
      };
      db.collection(USER_NOTES_COLLECTION_NAME)
        .doc(docId)
        .set(docData)
        .catch((err) => console.error("useListingNotes save error", err));
    },
    [listingId, userEmail]
  );

  // All mutators operate on `myNotes`, NOT the merged `notes`. Teammates'
  // notes are present in `notes` for display only; rules also deny writes
  // to their docs from us, so any accidental mutation would fail.

  /**
   * Appends atomically instead of going through `saveNotes`, which rewrites the
   * whole array from `myNotes` — and `myNotes` is `[]` until listener 1
   * delivers its first snapshot. On the board the listener only mounts when the
   * popover OPENS, so a realtor who typed and hit Enter before that round trip
   * finished replaced the document with just the new note and silently lost the
   * earlier ones ("sometimes it adds, but they don't show up"). `arrayUnion`
   * has no read-modify-write to lose, and also makes two open tabs safe.
   *
   * Order doesn't matter: the merged list below is sorted by `noteTime`.
   * `merge: true` creates the doc on the first note. A new note starts SHARED
   * with the author's offices (`withDefaultNoteShare`), so those offices are
   * `arrayUnion`ed into the doc-level union too — same atomic shape, no read.
   */
  const addNote = useCallback(
    (text: string) => {
      if (!text.trim() || !listingId || !userEmail) return;
      const newNote = withDefaultNoteShare(
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          note: text.trim(),
          noteTime: new Date()
        },
        organizations
      );
      const docId = userNotesDocId({ userId: userEmail, listingId });
      db.collection(USER_NOTES_COLLECTION_NAME)
        .doc(docId)
        .set(
          {
            userId: userEmail,
            listingId,
            notes: firebase.firestore.FieldValue.arrayUnion(newNote),
            ...(newNote.organizationIds
              ? { organizationIds: firebase.firestore.FieldValue.arrayUnion(...newNote.organizationIds) }
              : {})
          },
          { merge: true }
        )
        .catch((err) => console.error("useListingNotes add error", err));
    },
    [listingId, userEmail, organizations]
  );

  const updateNote = useCallback(
    (noteId: string, text: string) => {
      if (!myNotes.some((n) => n.id === noteId)) return;
      const updated = myNotes.map((n) =>
        n.id === noteId ? { ...n, note: text.trim(), noteTime: new Date() } : n
      );
      saveNotes(updated);
    },
    [myNotes, saveNotes]
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      if (!myNotes.some((n) => n.id === noteId)) return;
      saveNotes(myNotes.filter((n) => n.id !== noteId));
    },
    [myNotes, saveNotes]
  );

  /**
   * Toggle whether one of the user's OWN notes is shared with their org.
   * No-op for teammates' notes (they're not in `myNotes`); rules would
   * deny the write anyway.
   *
   * Shared = with every office the author is in; private = with nobody, the
   * NOTE's `organizationIds` dropped entirely (never `[]`) — see
   * `utils/noteSharing.ts`.
   */
  const setNoteOrgShare = useCallback(
    (noteId: string, share: boolean) => {
      if (!organizationId) return;
      if (!myNotes.some((n) => n.id === noteId)) return;
      saveNotes(myNotes.map((n) => (n.id === noteId ? applyNoteShare(n, share, organizations) : n)));
    },
    [myNotes, organizationId, organizations, saveNotes]
  );

  return { notes, hasNotes, isLoading, addNote, updateNote, deleteNote, setNoteOrgShare };
}

/**
 * Returns a Set<string> of listingIds that have at least one note visible to
 * the current user — either their own notes OR a teammate's org-shared note.
 *
 * Two listeners (own + org-shared) instead of one query because Firestore
 * can't OR across different fields (`userId == me` OR
 * `organizationIds array-contains myOrg`). We merge the sets in JS and emit
 * the union. Per-row code reads the cached Set instead of opening its own
 * listener — one Firestore listener per row didn't scale to ~200 hits/page.
 */
export function useListingsWithNotes(): { listingIdsWithNotes: Set<string>; isReady: boolean } {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId } = useFirestore();

  const [ids, setIds] = useState<Set<string>>(new Set());
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!userEmail) return;

    const mineRef: { current: Set<string> } = { current: new Set() };
    const orgRef: { current: Set<string> } = { current: new Set() };

    const emit = () => {
      const union = new Set<string>();
      mineRef.current.forEach((id) => union.add(id));
      orgRef.current.forEach((id) => union.add(id));
      setIds(union);
      setIsReady(true);
    };

    const collectListingIds = (snap: firebase.firestore.QuerySnapshot) => {
      const next = new Set<string>();
      snap.docs.forEach((d) => {
        const data = d.data() as UserNotesDocument;
        if (data.listingId && (data.notes?.length ?? 0) > 0) {
          next.add(data.listingId);
        }
      });
      return next;
    };

    const unsubMine = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("userId", "==", userEmail)
      .onSnapshot(
        (snap) => {
          mineRef.current = collectListingIds(snap);
          emit();
        },
        (error) => {
          console.error("useListingsWithNotes own-notes error", error);
          setIsReady(true);
        }
      );

    let unsubOrg = () => {};
    if (organizationId) {
      unsubOrg = db
        .collection(USER_NOTES_COLLECTION_NAME)
        .where("organizationIds", "array-contains", organizationId)
        .onSnapshot(
          (snap) => {
            orgRef.current = collectListingIds(snap);
            emit();
          },
          (error) => {
            console.error("useListingsWithNotes org-notes error", error);
          }
        );
    }

    return () => {
      unsubMine();
      unsubOrg();
    };
  }, [userEmail, organizationId]);

  return { listingIdsWithNotes: ids, isReady };
}
